import { useState, useEffect, useCallback } from 'react';
import {
  Loading,
  useNotify,
  ResourceContextProvider,
  ListContextProvider,
  Datagrid,
  TextField,
  FunctionField,
} from 'react-admin';
import {
  Box,
  Typography,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import { format } from 'date-fns';
import { supabase } from '../../../services/supabase';

interface UserInsertsListProps {
  userId: string;
  refreshKey?: number;
}

interface InsertEntry {
  id: string;
  slug: string;
  seenAt: string;
}

export const UserInsertsList = ({
  userId,
  refreshKey,
}: UserInsertsListProps) => {
  const notify = useNotify();
  const [entries, setEntries] = useState<InsertEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [deletingSlug, setDeletingSlug] = useState<string | null>(null);
  const [deleteConfirm, setDeleteConfirm] = useState<string | null>(null);

  const fetchInsertsSeen = useCallback(async () => {
    try {
      setLoading(true);

      const { data, error } = await supabase
        .from('user_profiles')
        .select('inserts_seen')
        .eq('user_id', userId)
        .single();

      if (error) throw error;

      const insertsSeen = (data?.inserts_seen ?? {}) as Record<string, string>;

      const parsed: InsertEntry[] = Object.entries(insertsSeen)
        .map(([slug, seenAt]) => ({ id: slug, slug, seenAt }))
        .sort(
          (a, b) =>
            new Date(b.seenAt).getTime() - new Date(a.seenAt).getTime()
        );

      setEntries(parsed);
    } catch {
      setEntries([]);
    } finally {
      setLoading(false);
    }
  }, [userId]);

  useEffect(() => {
    fetchInsertsSeen();
  }, [fetchInsertsSeen, refreshKey]);

  const handleDelete = async (slugToDelete: string) => {
    try {
      setDeletingSlug(slugToDelete);

      // Fetch current value to avoid stale overwrites
      const { data, error: fetchError } = await supabase
        .from('user_profiles')
        .select('inserts_seen')
        .eq('user_id', userId)
        .single();

      if (fetchError) throw fetchError;

      const current = (data?.inserts_seen ?? {}) as Record<string, string>;
      delete current[slugToDelete];

      const { error: updateError } = await supabase
        .from('user_profiles')
        .update({ inserts_seen: current })
        .eq('user_id', userId);

      if (updateError) throw updateError;

      setEntries(prev => prev.filter(e => e.slug !== slugToDelete));
      notify('Insert removed', { type: 'success' });
    } catch {
      notify('Failed to remove insert', { type: 'error' });
    } finally {
      setDeletingSlug(null);
    }
  };

  if (loading) {
    return <Loading />;
  }

  if (entries.length === 0) {
    return (
      <Box sx={{ p: 2 }}>
        <Typography variant="body2" color="text.secondary">
          No inserts seen by this user
        </Typography>
      </Box>
    );
  }

  return (
    <>
      <ResourceContextProvider value="user_inserts_seen">
      <ListContextProvider
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        value={{
          data: entries,
          total: entries.length,
          isLoading: loading,
          isFetching: false,
          page: 1,
          perPage: entries.length,
          setPage: () => {},
          setPerPage: () => {},
          setFilters: () => {},
          displayedFilters: {},
          filterValues: {},
          showFilter: () => {},
          hideFilter: () => {},
          sort: { field: 'seenAt', order: 'DESC' as const },
          setSort: () => {},
          resource: 'user_inserts_seen',
          refetch: () => {},
          selectedIds: [],
          onSelect: () => {},
          onToggleItem: () => {},
          onUnselectItems: () => {},
        } as any}
      >
        <Datagrid bulkActionButtons={false} size="small" rowClick={false}>
          <TextField source="slug" label="Insert Slug" />
          <FunctionField
            source="seenAt"
            label="Seen At"
            render={(record: InsertEntry) =>
              format(new Date(record.seenAt), 'yyyy-MM-dd HH:mm:ss')
            }
          />
          <FunctionField
            label=""
            render={(record: InsertEntry) => (
              <IconButton
                size="small"
                color="error"
                onClick={e => {
                  e.stopPropagation();
                  setDeleteConfirm(record.slug);
                }}
                disabled={deletingSlug === record.slug}
              >
                <DeleteIcon fontSize="small" />
              </IconButton>
            )}
          />
        </Datagrid>
      </ListContextProvider>
      </ResourceContextProvider>

      {/* Delete Confirmation */}
      <Dialog open={!!deleteConfirm} onClose={() => setDeleteConfirm(null)}>
        <DialogTitle>Remove seen insert?</DialogTitle>
        <DialogContent>
          <Typography>
            Remove <strong>{deleteConfirm}</strong> from this user&apos;s seen
            inserts? They will see this insert again.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteConfirm(null)}>Cancel</Button>
          <Button
            color="error"
            variant="contained"
            disabled={!!deletingSlug}
            onClick={async () => {
              if (deleteConfirm) {
                await handleDelete(deleteConfirm);
                setDeleteConfirm(null);
              }
            }}
          >
            Remove
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};
